import React, { useState, useCallback, useEffect } from "react"
import { MermaidDiagram, looksLikeMermaid } from "./mermaidCharts"

function PpAiImage({ src, alt }) {
    const [popupOpen, setPopupOpen] = useState(false)
    const close = useCallback(() => setPopupOpen(false), [])
    useEffect(() => {
        if (!popupOpen) return
        const onKey = (e) => {
            if (e.key === "Escape") close()
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    }, [popupOpen, close])
    if (!src) return null
    return (
        <>
            <button type="button" className="pp-ai-md-image-btn" onClick={() => setPopupOpen(true)} title="Vergrößern">
                <img className="pp-ai-md-image" src={src} alt={alt || ""} loading="lazy" />
            </button>
            {popupOpen && (
                <div className="pp-ai-image-popup-overlay">
                    <button
                        type="button"
                        className="pp-ai-image-popup-backdrop"
                        onClick={close}
                        aria-label="Schließen"
                    />
                    <div className="pp-ai-image-popup">
                        <img src={src} alt={alt || ""} />
                        <button
                            type="button"
                            className="pp-ai-image-popup-close"
                            onClick={close}
                            aria-label="Schließen"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

function isImageUrl(url) {
    if (!url || typeof url !== "string") return false
    const u = url.split("?")[0].trim().toLowerCase()
    return (
        /\.(jpe?g|png|gif|webp|svg|bmp)(\s|$)/i.test(u) ||
        /\/packshot\/|\/images?\/|media\.|img\.|image\.|cdn.*\.(jpg|jpeg|png|gif|webp)/i.test(url)
    )
}

function resolveIdealoHashUrl(url) {
    if (url && typeof url === "string" && url.includes("idealohash://")) {
        return "/resolveIdealoRedirects?hash=" + url.replace("idealohash://", "")
    }
    return url
}

function renderTextWithBold(text, offerLinkLabel = "Zum Angebot", allLinksAsOffer = false) {
    const pattern =
        /!\[([^\]]*)\]\(([^)]+)\)|\[(.*?)\]\(([^)]+)\)|\*\*(.+?)\*\*|((?:https?|idealohash):\/\/[^\s)]+)|`?(\/exporte\/[^\s)`\]]+)`?/g
    const nodes = []
    let lastIndex = 0
    let match
    let key = 0
    const isOfferUrl = (url) => /relocator\/relocate|offerKey=|productid=|price=|sid=|type=offer/i.test(url)
    const linkClass = (url) =>
        allLinksAsOffer || isOfferUrl(url) ? "pp-ai-md-link pp-ai-md-link-offer" : "pp-ai-md-link"
    const offerLabel = (url, fallbackLabel) => (allLinksAsOffer || isOfferUrl(url) ? offerLinkLabel : fallbackLabel)
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(<React.Fragment key={`t-${key++}`}>{text.slice(lastIndex, match.index)}</React.Fragment>)
        }
        if (match[1] && match[2]) {
            nodes.push(<PpAiImage key={`i-${key++}`} src={match[2]} alt={match[1]} />)
        } else if (match[3] && match[4]) {
            const url = match[4]
            if (isImageUrl(url)) {
                nodes.push(<PpAiImage key={`i-${key++}`} src={url} alt={match[3] || ""} />)
            } else {
                const resolvedUrl = resolveIdealoHashUrl(url)
                const defaultLabel =
                    match[3].toLowerCase() === "link" || match[3].toLowerCase() === "url" || match[3] === match[4]
                        ? url
                        : match[3]
                const label = offerLabel(url, defaultLabel)
                nodes.push(
                    <a
                        key={`a-${key++}`}
                        className={linkClass(url)}
                        href={resolvedUrl}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {label}
                    </a>
                )
            }
        } else if (match[5]) {
            nodes.push(<strong key={`b-${key++}`}>{match[5]}</strong>)
        } else if (match[6]) {
            const url = match[6]
            if (isImageUrl(url)) {
                nodes.push(<PpAiImage key={`i-${key++}`} src={url} alt="" />)
            } else {
                const resolvedUrl = resolveIdealoHashUrl(url)
                nodes.push(
                    <a
                        key={`a-${key++}`}
                        className={linkClass(url)}
                        href={resolvedUrl}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {offerLabel(url, url)}
                    </a>
                )
            }
        } else if (match[7]) {
            const path = match[7].replace(/^["']|["']$/g, "").trim()
            const fileName = path.replace(/^.*\//, "") || path
            nodes.push(
                <a
                    key={`e-${key++}`}
                    className="pp-ai-md-link pp-ai-export-link"
                    href={path}
                    download={fileName}
                    rel="noreferrer"
                    title={fileName}
                >
                    <i className="fas fa-file-csv" aria-hidden="true" />
                    <span className="pp-ai-export-link-label">{fileName}</span>
                </a>
            )
        }
        lastIndex = pattern.lastIndex
    }
    if (lastIndex < text.length) {
        nodes.push(<React.Fragment key={`t-${key++}`}>{text.slice(lastIndex)}</React.Fragment>)
    }
    return nodes
}

function splitMarkdownTableCells(line) {
    const cells = []
    let current = ""
    let i = 0

    const appendCell = () => {
        cells.push(current.trim())
        current = ""
    }

    while (i < line.length) {
        const rest = line.slice(i)

        if (rest.startsWith("\\|")) {
            current += "|"
            i += 2
            continue
        }

        if (rest[0] === "|") {
            appendCell()
            i += 1
            continue
        }

        const linkMatch = rest.match(/^\[([^\]]*)\]\(([^)]*)\)/)
        if (linkMatch) {
            current += linkMatch[0]
            i += linkMatch[0].length
            continue
        }

        const urlMatch = rest.match(/^((?:https?|idealohash):\/\/\S+)/)
        if (urlMatch) {
            current += urlMatch[1]
            i += urlMatch[1].length
            continue
        }

        current += rest[0]
        i += 1
    }

    appendCell()
    return cells.filter((c) => c.length > 0)
}

function renderMarkdownBlock(text, offerLinkLabel = "Zum Angebot", allLinksAsOffer = false) {
    const lines = text.split(/\r?\n/)
    const blocks = []
    let i = 0
    while (i < lines.length) {
        const line = lines[i]
        if (!line.trim()) {
            blocks.push(<div key={`br-${i}`} className="pp-ai-md-spacer" />)
            i += 1
            continue
        }
        if (/^#{1,3}\s+/.test(line)) {
            const level = line.match(/^#{1,3}/)[0].length
            const content = line.replace(/^#{1,3}\s+/, "")
            const HeadingTag = level === 1 ? "h5" : (level === 2 ? "h6" : "div")
            blocks.push(
                <HeadingTag key={`h-${i}`} className="pp-ai-md-heading">
                    {renderTextWithBold(content, offerLinkLabel, allLinksAsOffer)}
                </HeadingTag>
            )
            i += 1
            continue
        }
        if (line.includes("|") && i + 1 < lines.length && lines[i + 1].includes("|")) {
            const headerLine = lines[i]
            const sepLine = lines[i + 1]
            if (/^\s*\|?[-:\s|]+\|?\s*$/.test(sepLine)) {
                const headerCells = splitMarkdownTableCells(headerLine)
                const rows = []
                i += 2
                while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
                    rows.push(splitMarkdownTableCells(lines[i]))
                    i += 1
                }
                blocks.push(
                    <div key={`tbl-${i}`} className="pp-ai-md-table-wrap">
                        <table className="pp-ai-md-table">
                            <thead>
                                <tr>
                                    {headerCells.map((cell, idx) => (
                                        <th key={`th-${idx}`}>
                                            {renderTextWithBold(cell, offerLinkLabel, allLinksAsOffer)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, rIdx) => (
                                    <tr key={`tr-${rIdx}`}>
                                        {row.map((cell, cIdx) => (
                                            <td key={`td-${rIdx}-${cIdx}`}>
                                                {renderTextWithBold(cell, offerLinkLabel, allLinksAsOffer)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
                continue
            }
        }
        if (/^(-|\*)\s+/.test(line)) {
            const items = []
            while (i < lines.length && /^(-|\*)\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^(-|\*)\s+/, ""))
                i += 1
            }
            blocks.push(
                <ul key={`ul-${i}`} className="pp-ai-md-list">
                    {items.map((item, idx) => (
                        <li key={`li-${idx}`}>{renderTextWithBold(item, offerLinkLabel, allLinksAsOffer)}</li>
                    ))}
                </ul>
            )
            continue
        }
        if (/^\d+\.\s+/.test(line)) {
            const items = []
            while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\d+\.\s+/, ""))
                i += 1
            }
            blocks.push(
                <ol key={`ol-${i}`} className="pp-ai-md-list">
                    {items.map((item, idx) => (
                        <li key={`oli-${idx}`}>{renderTextWithBold(item, offerLinkLabel, allLinksAsOffer)}</li>
                    ))}
                </ol>
            )
            continue
        }
        blocks.push(
            <p key={`p-${i}`} className="pp-ai-md-paragraph">
                {renderTextWithBold(line, offerLinkLabel, allLinksAsOffer)}
            </p>
        )
        i += 1
    }
    return blocks
}

function renderFormattedContent(content, offerLinkLabel = "Zum Angebot", allLinksAsOffer = false) {
    const segments = content.split(/```([\s\S]*?)```/g)
    return segments.map((segment, index) => {
        const isCode = index % 2 === 1
        if (isCode) {
            const trimmed = segment.trimStart()
            const isMermaidBlock = trimmed.toLowerCase().startsWith("mermaid")
            const mermaidCode = isMermaidBlock ? trimmed.replace(/^\s*mermaid\s*\n?/i, "").trim() : ""
            if (isMermaidBlock && mermaidCode && looksLikeMermaid(mermaidCode)) {
                return <MermaidDiagram key={`m-${index}`} code={mermaidCode} />
            }
            return (
                <pre key={`c-${index}`} className="pp-ai-code">
                    <code>{segment}</code>
                </pre>
            )
        }
        return <div key={`s-${index}`}>{renderMarkdownBlock(segment, offerLinkLabel, allLinksAsOffer)}</div>
    })
}

export { renderFormattedContent, renderMarkdownBlock, renderTextWithBold }
